import { DataQualityType } from '@tableau/data-catalog-client-components';
import { OtherDivProps } from '@tableau/vizportalclient-common-components';
import {
  CompactInlineAlertProps,
  CompactInlineAlertWidget,
  DropdownButtonType,
  getMaxCharValidation,
  List,
  RadioButtonWithLabelGroupWidget,
  RadioButtonWithLabelOption,
  SingleSelectDropdown,
  TabStyles,
  TextLinkWidget,
  ToggleSwitchWidget
} from '@tableau/widgets';
import { AlertType } from '@tableau/widgets/lib/src/widgets/AlertStyles';
import React from 'react';
import shallowEqual from 'shallowequal';
import { NestedCSSProperties } from 'typestyle/lib/types';

import { ReactActionDialogBodyProps, ReactActionDialogOptions } from '~commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { HelpService } from '~commonInfra/services/HelpService';
import { LabelContentItem } from '~src/catalog/restApi/labels/LabelTypes';
import { makeFetchInitialStateForLabels } from '~src/catalog/restApi/labels/useLabels';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { cssClass } from '~src/style';

import { Messages } from '../../../codegen/Localize';
import {
  ContentItemNameDiv,
  DATA_QUALITY_DIALOG_HELP_TEST_ID,
  DATA_QUALITY_DIALOG_MESSAGE_ID,
  DATA_QUALITY_DIALOG_TYPE_TEST_ID,
  dropDownStyle,
  markupEditorStyle
} from '../../styles/DataQualityStyles';
import { TextFormatterOrAreaWidget } from '../common/TextFormatterOrAreaWidget';
import {
  dataQualityTypeListItem,
  dataQualityTypeToString,
  userEditableDataQualityTypes
} from './DataQualityType';

const MESSAGE_MAXIMUM_LENGTH: number = 4000;

export const defaultInitialState: WarningDialogState = {
  isActive: false,
  isSevere: false,
  type: dataQualityTypeToString(DataQualityType.WARNING),
  message: '',
};

export interface DataQualityDialogBodyProps {
  readonly contentItem: LabelContentItem;
  readonly loading?: boolean;
}

export interface WarningDialogState {
  readonly luid?: string;
  readonly isActive: boolean;
  readonly isSevere: boolean;
  readonly type: string;
  readonly message: string;
}

function isTextAreaValid(currentFormData: { message: string, isActive: boolean }): boolean {
  return (!currentFormData.isActive || currentFormData.message.length > 0) && currentFormData.message.length <= MESSAGE_MAXIMUM_LENGTH;
}

namespace HelpText {
  const prefix = 'help-text-div';

  const base: NestedCSSProperties = {
    paddingTop: TabStyles.Spacing.DefaultSpace,
  };

  const enabledClass = cssClass(prefix, 'enabled', base);

  const disabledClass = cssClass(prefix, 'disabled', base, {
    opacity: TabStyles.Opacity.Disabled,
  });

  export function Div({ children, disabled }: OtherDivProps & { readonly disabled?: boolean }) {
    return (
      <div className={disabled ? disabledClass : enabledClass}>{children}</div>
    );
  }
}

namespace InputHint {
  const prefix = 'input-hint-div';

  const base: NestedCSSProperties = {
    paddingTop: TabStyles.Spacing.MediumSpace,
    fontSize: TabStyles.Typography.Sizing.Default,
    color: TabStyles.Typography.Color.PrimaryLight,
  };

  const enabledClass = cssClass(prefix, 'enabled', base);

  const disabledClass = cssClass(prefix, 'disabled', base, {
    opacity: TabStyles.Opacity.Disabled,
  });

  export function Div({ children, disabled }: OtherDivProps & { readonly disabled?: boolean }) {
    return (
      <div className={disabled ? disabledClass : enabledClass}>{children}</div>
    );
  }
}

type WarningDialogStateComparison = Omit<WarningDialogState, 'luid'>

function isFormDataValidForSubmission(args: {
  initialFormData: WarningDialogStateComparison;
  currentFormData: WarningDialogStateComparison;
  additionalValidation: (current: WarningDialogStateComparison) => boolean;
}): boolean {
  const { initialFormData, currentFormData } = args;

  const formIsDirty = !shallowEqual(initialFormData, currentFormData);
  if (!formIsDirty) {
    return false;
  }

  if (!args.additionalValidation(currentFormData)) {
    return false;
  }

  return true;
}

const sampleInlineAlertWidgetStyles = cssClass('data-quality-dialog', 'sample-inline-alert', {
  marginTop: '8px',
  marginBottom: '8px',
});

function SampleCompactInlineAlertWidget({ type, label, hint }: Pick<CompactInlineAlertProps, 'type'> & {
  readonly label: string;
  readonly hint?: JSX.Element;
}): JSX.Element {
  return (
    <span style={{ marginTop: '4px', marginBottom: '4px' }}>
      <div style={{ marginRight: '16px' }}>{label}</div>
      <CompactInlineAlertWidget
        className={sampleInlineAlertWidgetStyles}
        type={type}
        testId={`warning-option-sample-${type}`}
        style={{ width: '180px' }}
      >{Messages.dataQualityDialog_v2_SampleText()}</CompactInlineAlertWidget>
      {hint && hint}
    </span>
  );
}

export function SevereWarningContent({ disabled, isSevere, setIsSevere }: {
  disabled: boolean;
  isSevere: boolean;
  setIsSevere: (b: boolean) => void;
}): JSX.Element | null {
  const buttonList: RadioButtonWithLabelOption[] = [
    {
      label: (<SampleCompactInlineAlertWidget
        label={Messages.dataQualityDialog_v2_StandardVisibility()}
        type={AlertType.Info}
      />),
      testId: 'warning-standard-option',
      value: 'standard',
    },
    {
      label: (<SampleCompactInlineAlertWidget
        label={Messages.dataQualityDialog_v2_HighVisibility()}
        type={AlertType.Warning}
        hint={<HelpText.Div><div >{Messages.dataQualityDialog_v2_HighVisibilityHelp()}</div></HelpText.Div>}
      />),
      testId: 'warning-severe-option',
      value: 'severe',
    },
  ];

  return (
    <RadioButtonWithLabelGroupWidget
      removeLeftMargin={true}
      checkedIndex={buttonList.findIndex(b => b.value === (isSevere ? 'severe' : 'standard'))}
      handleChange={!disabled ? (value) => { setIsSevere(value === 'severe'); } : undefined}
      buttonList={buttonList}
      label={<HelpText.Div>{Messages.dataQualityDialog_v2_SetVisibilityLevel()}</HelpText.Div>}
      style={{ width: '100%', marginBottom: '8px', opacity: disabled ? TabStyles.Opacity.Disabled : undefined }}
    />
  );
}

const horizontalLine: React.CSSProperties = {
  border: '0.25px solid #e6e6e6',
  marginTop: '16px',
  marginBottom: '16px',
};

export type DataQualityActionDialogOptions = ReactActionDialogOptions<DataQualityDialogBodyProps, WarningDialogState, unknown>;
export type DataQualityDialogBodyComponentProps = ReactActionDialogBodyProps<DataQualityDialogBodyProps, WarningDialogState>;

export function DataQualityDialogBody({ updateDialogState, dialogState, contentItem }: DataQualityDialogBodyComponentProps): React.ReactElement {
  const handleMessageChange = React.useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => updateDialogState({ message: e.currentTarget.value }), [updateDialogState]);
  const { message, valid } = getMaxCharValidation(dialogState.message, MESSAGE_MAXIMUM_LENGTH);
  const disabled = !dialogState.isActive;

  return (
    <>
      <ContentItemNameDiv>{contentItem.contentName}</ContentItemNameDiv>
      <HelpText.Div>
        {Messages.dataQualityDialog_v2_Help()}
        <div style={{ marginBottom: '16px' }}>
          <TextLinkWidget
            href={HelpService.getServerHelpUrlByCategory('dqw-manual')}
            testId={DATA_QUALITY_DIALOG_HELP_TEST_ID}
            target="_blank" rel="noopener noreferrer"
          >
            {Messages.dataQuality_learnMore()}
          </TextLinkWidget>
        </div>
        <hr style={horizontalLine} />
      </HelpText.Div>
      <ToggleSwitchWidget
        textAlign={'right'}
        onChange={(e) => updateDialogState({ isActive: e.target.checked })}
        checked={dialogState.isActive}
        children={Messages.dataQualityDialog_v2_ShowWarning()}
        style={{ justifyContent: 'flex-start', marginBottom: '8px' }}
      />
      <HelpText.Div disabled={disabled}>{Messages.dataQualityDialog_v2_SelectLabel()}</HelpText.Div>
      <SingleSelectDropdown
        testId={DATA_QUALITY_DIALOG_TYPE_TEST_ID}
        type={DropdownButtonType.Outline}
        list={DataQualityWarningTypeList()}
        disabled={disabled}
        value={dialogState.type}
        onChange={(type?: string) => updateDialogState({ type })}
        style={dropDownStyle}
        label={''} />
      <SevereWarningContent
        disabled={disabled}
        isSevere={dialogState.isSevere}
        setIsSevere={(isSevere: boolean) => updateDialogState({ isSevere })}
      />

      <TextFormatterOrAreaWidget
        label={<InputHint.Div disabled={disabled}>{Messages.dataQualityMessage()}</InputHint.Div>}
        onChange={handleMessageChange}
        id={DATA_QUALITY_DIALOG_MESSAGE_ID}
        testId={DATA_QUALITY_DIALOG_MESSAGE_ID}
        disabled={disabled}
        valid={valid}
        message={message}
        style={markupEditorStyle}
        markup={dialogState.message}
      />
    </>
  );
}

const fetchInitialState = makeFetchInitialStateForLabels<DataQualityDialogBodyProps, WarningDialogState>({
  category: 'warning',
  transform: (t) => ({
    isActive: t.active,
    isSevere: t.elevated,
    message: t.message,
    type: t.value,
  }),
});

const DataQualityWarningTypeList = () =>
  <List items={userEditableDataQualityTypes.filter(type => type !== DataQualityType.CERTIFIED).map(dataQualityTypeListItem)} />;

export function getDataQualityActionDialogOptions(args: {
  initialBodyProps: DataQualityDialogBodyProps;
  initialFormData: WarningDialogState;
  doAction: (formData: WarningDialogState) => Promise<unknown>;
}): DataQualityActionDialogOptions {
  const { initialBodyProps, initialFormData, doAction } = args;
  const bodyComponent = DataQualityDialogBody;

  return {
    title: Messages.dataQualityDialog_Title(),
    fetchInitialState: FeatureFlags.columnDQWsEnabled ? fetchInitialState : undefined,
    bodyComponent,
    initialDialogState: initialFormData,
    initialBodyProps: initialBodyProps,
    actionName: Messages.save(),
    action: (_, dialogState, __) => doAction(dialogState),
    actionDisabled: (_, dialogState, fetchedState) => !isFormDataValidForSubmission({
      initialFormData: fetchedState ?? initialFormData,
      currentFormData: dialogState,
      additionalValidation: isTextAreaValid
    }),
    showResetButtonUsingState: FeatureFlags.columnDQWsEnabled ? defaultInitialState : undefined,
    dismissDialogOnActionFailed: true,
    dialogWidth: TabDialogWidth.width500px,
  };
}
